import {
  addDays,
  compareAsc,
  differenceInCalendarDays,
  format,
  getISOWeek,
  parseISO,
} from 'date-fns'

import { CalendarGridMode } from '../../constants/calendarGrid'
import { EMPTY_LIST } from '../../constants/common'
import {
  DATE_API_DATA_FORMAT_SHORT,
  DATE_WEEKDAY_SHORT_FORMAT,
} from '../../constants/date'

import { CALENDAR_GRID_DROP_AREA_PREFIX } from './CalendarGrid.constants'
import { getCalendarGridItemDay } from './CalendarGrid.utils.time'

function getCalendarGridEntryItems(item) {
  return item.aggregatedItems ?? [item]
}

export function getCalendarGridTimedItems(items) {
  return items.filter(item => !item.isAllDay)
}

export function getCalendarGridAllDayItems(items) {
  return items.filter(item => item.isAllDay)
}

export function getIsCalendarGridMonthMode(mode) {
  return mode === CalendarGridMode.month
}

export function getIsCalendarGridAgendaMode(mode) {
  return mode === CalendarGridMode.agenda
}

export function getIsCalendarGridToday({ day, today }) {
  return day.date === today
}

export function getIsCalendarGridItemsHidden({ isRangeLoading, serverError }) {
  return isRangeLoading || Boolean(serverError)
}

export function getIsCalendarGridBodyMessageShown({
  bodyMessage,
  isAgendaMode,
  isRangeLoading,
}) {
  return Boolean(bodyMessage) && !isAgendaMode && !isRangeLoading
}

export function getCalendarGridWeekNumber(day) {
  return getISOWeek(parseISO(day.date))
}

export function formatCalendarGridDay({ dateFormat, day, locale }) {
  return format(parseISO(day), dateFormat, { locale })
}

export function getIsCalendarGridMultiDayItem(item) {
  return Boolean(item.isAllDay && item.lastDate && item.lastDate > item.date)
}

export function getCalendarGridItemDays({ item, locale, timezone }) {
  const firstDay = getCalendarGridItemDay({ item, locale, timezone })

  return getIsCalendarGridMultiDayItem(item)
    ? Array.from(
        {
          length:
            differenceInCalendarDays(
              parseISO(item.lastDate),
              parseISO(firstDay)
            ) + 1,
        },
        (_, index) =>
          format(addDays(parseISO(firstDay), index), DATE_API_DATA_FORMAT_SHORT)
      )
    : [firstDay]
}

export function getCalendarGridItemCardDayLabel({ item, locale, timezone }) {
  const days = getCalendarGridItemDays({ item, locale, timezone })

  return formatCalendarGridDay({
    dateFormat: DATE_WEEKDAY_SHORT_FORMAT,
    day: days[days.length - 1],
    locale,
  })
}

export function getCalendarGridItemsByDay({ items, locale, timezone }) {
  return items.reduce((acc, item) => {
    getCalendarGridItemDays({ item, locale, timezone }).forEach(day => {
      acc[day] = [...(acc[day] ?? EMPTY_LIST), item]
    })

    return acc
  }, {})
}

export function getCalendarGridItemsSortedByStart(items) {
  return [...items].sort((left, right) =>
    compareAsc(parseISO(left.start), parseISO(right.start))
  )
}

export function getCalendarGridExpandedItems(items) {
  return items.flatMap(getCalendarGridEntryItems)
}

export function getCalendarGridItemsInDayOrder(items) {
  return [
    ...getCalendarGridAllDayItems(items),
    ...getCalendarGridItemsSortedByStart(getCalendarGridTimedItems(items)),
  ]
}

export function getCalendarGridChipTitle(item) {
  return item.chip?.title ?? item.title
}

export function getCalendarGridPopoverTitle(item) {
  return item.popover?.title ?? item.title
}

export function getCalendarGridPopoverSecondaryLabel(item) {
  return item.popover && 'secondaryLabel' in item.popover
    ? item.popover.secondaryLabel
    : item.secondaryLabel
}

export function getIsCalendarGridSecondaryLabelClickable({
  item,
  onClickSecondaryLabel,
}) {
  return Boolean(onClickSecondaryLabel && item.isSecondaryLabelClickable)
}

export function getIsCalendarGridPinnedSecondaryLabelClickable({
  item,
  onClickSecondaryLabel,
}) {
  return (
    Boolean(item.isSecondaryLabelPinned) &&
    getIsCalendarGridSecondaryLabelClickable({ item, onClickSecondaryLabel })
  )
}

export function getIsCalendarGridDayFull(dayLoad) {
  return Boolean(dayLoad?.isFull)
}

export function getCalendarGridDropAreaId(day) {
  return `${CALENDAR_GRID_DROP_AREA_PREFIX}${day}`
}

export function getCalendarGridSelectionBounds({ anchor, focus }) {
  return { from: Math.min(anchor, focus), to: Math.max(anchor, focus) }
}
